import { Plus } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateTargetDialog } from '@/features/memorization/create-target-dialog';
import { useIsPersonalSpace } from '@/features/personal-space/hooks';

/**
 * The solo learner's own target-setting affordance (ADR-031, Decision 6).
 *
 * Renders only when the Active Organization actually IS the caller's
 * personal space - so a santri viewing their pondok never sees it, which
 * matches what the server would do anyway: `POST
 * /memorization/personal-targets` refuses outside a PERSONAL
 * organization. This is presentation, not the security boundary
 * (`lib/capabilities.ts`'s own standing caveat).
 */
export function PersonalSpaceCard({
  studentId,
}: {
  readonly studentId: string;
}): JSX.Element | null {
  const isPersonalSpace = useIsPersonalSpace();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!isPersonalSpace) {
    return null;
  }

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle className="text-base">Ruang hafalan pribadi Anda</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="leading-relaxed text-muted-foreground">
          Di sini Anda menentukan sendiri apa yang dihafal dan mencatat setoran Anda sendiri. Untuk
          diuji dan dibimbing seorang ustadz, bergabunglah ke pondok lewat undangan pengelolanya -
          ruang ini tetap milik Anda.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Tambah target hafalan
          </Button>
          {/* Without this the target a learner just set had nowhere to be
              seen from here - the list lived only inside the roster's
              per-student workspace. */}
          <Button asChild variant="outline">
            <Link to="/hafalan">Lihat hafalan saya</Link>
          </Button>
        </div>
      </CardContent>

      <CreateTargetDialog
        studentId={studentId}
        mode="personal"
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </Card>
  );
}
